import { useCallback, useEffect, useState } from 'react';
import { apiUrl } from '../../config/api';

// Categories are static for the length of a session and this hook is
// mounted by the header, the landing page and the filter panel at the
// same time. Caching the promise collapses those three identical
// requests into one.
let categoriesCache = null;
let inFlightRequest = null;

const loadCategories = (force = false) => {
  if (!force && categoriesCache) return Promise.resolve(categoriesCache);
  if (!force && inFlightRequest) return inFlightRequest;

  inFlightRequest = fetch(apiUrl('/api/category'), { method: 'GET' })
    .then((response) => {
      if (!response.ok) throw new Error('Network response was not ok');
      return response.json();
    })
    .then((data) => {
      categoriesCache = Array.isArray(data.category) ? data.category : [];
      return categoriesCache;
    })
    .finally(() => {
      inFlightRequest = null;
    });

  return inFlightRequest;
};

const useCategories = () => {
  const [categories, setCategories] = useState(() => categoriesCache || []);
  const [loading, setLoading] = useState(() => !categoriesCache);
  const [error, setError] = useState(null);

  const fetchCategories = useCallback(async (force = false) => {
    setLoading(true);
    try {
      const list = await loadCategories(force);
      setCategories(list);
      setError(null);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    let active = true;

    if (categoriesCache) {
      setCategories(categoriesCache);
      setLoading(false);
      return () => {
        active = false;
      };
    }

    loadCategories()
      .then((list) => {
        if (active) {
          setCategories(list);
          setError(null);
        }
      })
      .catch((err) => {
        if (active) setError(err.message);
      })
      .finally(() => {
        if (active) setLoading(false);
      });

    return () => {
      active = false;
    };
  }, []);

  return { categories, loading, error, fetchCategories };
};

export default useCategories;
